import React, { useState, useEffect } from 'react';
import {
    Shield,
    UserPlus,
    Plus,
    Loader,
    Trash2,
    Database,
    RefreshCw,
    Globe,
    AlertTriangle,
    Users,
    Shuffle,
    UserCheck,
    ChevronDown,
    ChevronUp,
    Zap,
    PhoneCall,
    Mail,
    MessageSquare,
    Bot,
    Bell,
    Search,
    Settings,
    Calendar,
    Edit2,
    CheckCircle,
    Copy,
    ExternalLink,
    Building,
    User,
    Key,
    Eye,
    EyeOff,
    ToggleLeft,
    ToggleRight,
    Lock,
    X,
    Briefcase,
    MapPin,
    Upload
} from 'lucide-react';

import { userService, settingsService, maintenanceService, sourcesService, servicesService, citiesService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';
import ConfirmModal from '../ui/ConfirmModal';
import CsvSetupModal from '../modals/CsvSetupModal';
import CsvImportModal from '../modals/CsvImportModal';
import { useCsvUpload } from '../../hooks/useCsvUpload';
import { ROLE_LABELS } from '../../lib/constants';

// Simple in-component toast
const ROLES = ['CEO', 'GM_Operations', 'Senior_Team_Lead', 'Team_Lead', 'Associate_Team_Lead'];
const useLocalToast = () => {
    const [toast, setToastState] = React.useState(null);
    const setToast = (msg, type = 'success') => {
        setToastState({ msg, type });
        setTimeout(() => setToastState(null), 3500);
    };
    return { toast, setToast };
};

// Helper component for copy button
const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <button
            onClick={handleCopy}
            className={`p-2 rounded-lg transition-colors ${copied ? 'bg-green-100 text-green-600' : 'hover:bg-slate-200 text-slate-600'}`}
            title="Copy Password"
        >
            {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
        </button>
    );
};

export default function AdminPanelView({ activeSection = 'users' }) {
    const { t } = useLanguage();
    const { toast, setToast } = useLocalToast();
    const { activeUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserName, setNewUserName] = useState('');
    const [newUserRole, setNewUserRole] = useState('Associate_Team_Lead');
    const [newUserParentId, setNewUserParentId] = useState('');
    const [inviteTab, setInviteTab] = useState('associate_team_lead');
    const [showInviteModal, setShowInviteModal] = useState(false);

    useEffect(() => {
        setInviteTab('associate_team_lead');
        setNewUserRole('Associate_Team_Lead');
    }, [activeUser]);

    const handleInviteTabChange = (tab) => {
        setInviteTab(tab);
        setNewUserName('');
        setNewUserEmail('');
        setNewUserParentId('');
        if (tab === 'associate_team_lead' || tab === 'associate') {
            setNewUserRole('Associate_Team_Lead');
        } else if (tab === 'gm_operations' || tab === 'bm_operations') {
            setNewUserRole('GM_Operations');
        } else if (tab === 'senior_team_lead') {
            setNewUserRole('Senior_Team_Lead');
        } else if (tab === 'team_lead') {
            setNewUserRole('Team_Lead');
        }
    };

    const canUserManage = (target) => {
        if (!activeUser || !target) return false;
        const currentUserId = Number(activeUser.id || activeUser.uid);
        const targetId = Number(target.id);

        if (targetId === currentUserId) return false; // Self toggle/edit disabled
        if (['CEO', 'GM_Operations', 'BM_Operations'].includes(activeUser.role)) return true;
        if (['Team_Lead', 'Senior_Team_Lead'].includes(activeUser.role)) {
            const isAssociate = ['Associate_Team_Lead', 'Associate'].includes(target.role);
            const isMyParent = target.parentId !== null && target.parentId !== undefined && Number(target.parentId) === currentUserId;
            const isMyCreated = target.createdById !== null && target.createdById !== undefined && Number(target.createdById) === currentUserId;
            return isAssociate && (isMyParent || isMyCreated);
        }
        return false;
    };
    const [batchTeamLeads, setBatchTeamLeads] = useState([{ name: '', email: '' }]);
    const [batchAssociates, setBatchAssociates] = useState([{ name: '', email: '', teamLeadIndex: 0, existingTeamPrequalificationId: '' }]);

    const handleAddBatchTeamLeadField = () => setBatchTeamLeads([...batchTeamLeads, { name: '', email: '' }]);
    const handleRemoveBatchTeamLeadField = (index) => {
        if (batchTeamLeads.length > 1) {
            const updated = [...batchTeamLeads];
            updated.splice(index, 1);
            setBatchTeamLeads(updated);
        }
    };
    const handleBatchTeamLeadChange = (index, field, value) => {
        const updated = [...batchTeamLeads];
        updated[index][field] = value;
        setBatchTeamLeads(updated);
    };
    const handleAddBatchAssociateField = () => setBatchAssociates([...batchAssociates, { name: '', email: '', teamLeadIndex: 0, existingTeamPrequalificationId: '' }]);
    const handleRemoveBatchAssociateField = (index) => {
        if (batchAssociates.length > 1) {
            const updated = [...batchAssociates];
            updated.splice(index, 1);
            setBatchAssociates(updated);
        }
    };
    const handleBatchAssociateChange = (index, field, value) => {
        const updated = [...batchAssociates];
        updated[index][field] = value;
        setBatchAssociates(updated);
    };
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [assignmentConfig, setAssignmentConfig] = useState({
        mode: 'round_robin',
        defaultAssignee: 'Unassigned',
        sourceRules: []
    });
    const [sources, setSources] = useState([]);
    const [automationRules, setAutomationRules] = useState({});
    const [tenderAutomationRules, setTenderAutomationRules] = useState({});
    const [profileAutomationRules, setProfileAutomationRules] = useState({});
    const [automationSearch, setAutomationSearch] = useState('');
    const [tenderAutomationSearch, setTenderAutomationSearch] = useState('');
    const [profileAutomationSearch, setProfileAutomationSearch] = useState('');
    const [eventTypes, setEventTypes] = useState([]);
    const [newEventType, setNewEventType] = useState('');
    const [editingEventType, setEditingEventType] = useState(null);
    const [venues, setVenues] = useState([]);
    const [newVenueName, setNewVenueName] = useState('');
    const [newVenueId, setNewVenueId] = useState('');
    const [statusModal, setStatusModal] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    // Password management state
    const [passwordModal, setPasswordModal] = useState(null); // { userId, userName, mode: 'view'|'change' }
    const [editUserModal, setEditUserModal] = useState(null);
    const [editUserLoading, setEditUserLoading] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [revealedPassword, setRevealedPassword] = useState(null);
    const [togglingUser, setTogglingUser] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, confirmText, danger, onConfirm }
    const [activeSettingsTab, setActiveSettingsTab] = useState('services');
    const [services, setServices] = useState([]);
    const [newServiceName, setNewServiceName] = useState('');
    const [editingServiceId, setEditingServiceId] = useState(null);
    const [editingServiceName, setEditingServiceName] = useState('');
    const [cities, setCities] = useState([]);
    const [citySearch, setCitySearch] = useState('');
    const [newCityName, setNewCityName] = useState('');

    const loadUsers = async () => {
        try {
            const data = await userService.getAll();
            setUsers(data);
        } catch (err) {
            console.error('Error fetching users', err);
        }
    };

    const {
        importProgress,
        closeImportModal,
        handleFileUpload
    } = useCsvUpload('users', () => loadUsers());

    const [showCsvSetup, setShowCsvSetup] = useState(false);
    const [showCsvImport, setShowCsvImport] = useState(false);

    const handleCsvUploadStart = (file) => {
        setShowCsvSetup(false);
        setShowCsvImport(true);
        handleFileUpload({ target: { files: [file] } });
    };

    // Load All Initial Data
    useEffect(() => {
        const loadAllData = async () => {
            setInitialLoading(true);
            try {
                // Run loads in parallel
                await Promise.all([
                    // Users
                    loadUsers(),

                    // Sources
                    sourcesService.getAll().then(setSources).catch(err => console.error('Error fetching sources', err)),

                    // Services
                    servicesService.getAll().then(setServices).catch(err => console.error('Error fetching services', err)),

                    // Settings
                    (async () => {
                        try {
                            const [assignRules, autoRules, tenderAutoRules, profileAutoRules, evts] = await Promise.all([
                                settingsService.get('assignment_rules'),
                                settingsService.get('automation_rules'),
                                settingsService.get('tender_automation_rules').catch(() => null),
                                settingsService.get('profile_automation_rules').catch(() => null),
                                Promise.resolve({ list: [] })
                            ]);

                            if (assignRules) setAssignmentConfig(assignRules);
                            if (autoRules) setAutomationRules(autoRules);
                            if (tenderAutoRules) setTenderAutomationRules(tenderAutoRules);
                            if (profileAutoRules) setProfileAutomationRules(profileAutoRules);

                            if (evts && evts.list && evts.list.length > 0) {
                                setEventTypes(evts.list);
                            } else {
                                setEventTypes([]);
                            }

                            const vens = null;
                            if (vens && vens.list && vens.list.length > 0) {
                                setVenues(vens.list);
                            } else {
                                const defaultVenues = [
                                    { id: 'marquee', name: 'AAA FM Marquee', isLive: true },
                                    { id: 'banquet', name: 'AAA FM Banquet Hall', isLive: true }
                                ];
                                setVenues(defaultVenues);
                            }

                            // Load cities from cities API
                            try {
                                const citiesData = await citiesService.getAll();
                                setCities(citiesData);
                            } catch(err) {
                                console.error('Error loading cities', err);
                                setCities([]);
                            }
                        } catch (err) {
                            console.error('Error loading settings', err);
                            setEventTypes([]);
                        }
                    })()
                ]);
            } finally {
                setInitialLoading(false);
            }
        };

        loadAllData();
    }, []);

    const handleSaveAssignmentRules = async () => {
        setLoading(true);
        try {
            await settingsService.update('assignment_rules', assignmentConfig);
            setToast(t('assignmentRulesSaved') || 'Assignment rules saved!', 'success');
        } catch (error) {
            console.error(error);
            setToast(t('failedToSaveRules') || 'Failed to save rules.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const addSourceRule = () => {
        setAssignmentConfig((prev) => ({
            ...prev,
            sourceRules: [...(prev.sourceRules || []), { source: '', assignmentType: 'round_robin_role', role: 'Sales', assignTo: '' }]
        }));
    };

    const updateSourceRule = (index, field, value) => {
        setAssignmentConfig((prev) => {
            const rules = [...(prev.sourceRules || [])];
            rules[index] = { ...rules[index], [field]: value };
            return { ...prev, sourceRules: rules };
        });
    };

    const removeSourceRule = (index) => {
        setAssignmentConfig((prev) => ({
            ...prev,
            sourceRules: (prev.sourceRules || []).filter((_, i) => i !== index)
        }));
    };

    const getAutomationRule = (sourceKey) => {
        return automationRules[sourceKey] || {
            addToCallList: true,
            sendNotification: true,
            emailResponse: false,
            browserNotification: false
        };
    };

    const getTenderAutomationRule = (sourceKey) => {
        return tenderAutomationRules[sourceKey] || {
            addToCallList: true,
            sendNotification: true,
            emailResponse: false,
            browserNotification: false
        };
    };

    const getProfileAutomationRule = (sourceKey) => {
        return profileAutomationRules[sourceKey] || {
            addToCallList: true,
            sendNotification: true,
            emailResponse: false,
            browserNotification: false
        };
    };

    const toggleTenderAutomation = (sourceKey, field) => {
        setTenderAutomationRules(prev => ({
            ...prev,
            [sourceKey]: {
                ...getTenderAutomationRule(sourceKey),
                [field]: !getTenderAutomationRule(sourceKey)[field]
            }
        }));
    };

    const toggleProfileAutomation = (sourceKey, field) => {
        setProfileAutomationRules(prev => ({
            ...prev,
            [sourceKey]: {
                ...getProfileAutomationRule(sourceKey),
                [field]: !getProfileAutomationRule(sourceKey)[field]
            }
        }));
    };

    const toggleAutomation = (sourceKey, field) => {
        setAutomationRules(prev => ({
            ...prev,
            [sourceKey]: {
                ...getAutomationRule(sourceKey),
                [field]: !getAutomationRule(sourceKey)[field]
            }
        }));
    };

    const handleSaveAutomationRules = async () => {
        setLoading(true);
        try {
            await Promise.all([
                settingsService.update('automation_rules', automationRules),
                settingsService.update('tender_automation_rules', tenderAutomationRules),
                settingsService.update('profile_automation_rules', profileAutomationRules)
            ]);
            setToast(t('automationRulesSaved') || 'Automation rules saved!', 'success');
        } catch (error) {
            console.error(error);
            setToast(t('failedToSaveAutomationRules') || 'Failed to save automation rules.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Event Type CRUD
    const saveEventTypes = async (newList) => {
        try {
            await settingsService.update('event_types', { list: newList });
            window.dispatchEvent(new CustomEvent('app_settings_updated'));
        } catch (err) {
            console.error('Failed to save event types', err);
            setToast(t('failedToSaveEventTypes') || 'Failed to save event types to server', 'error');
        }
    };

    const handleAddEventType = async () => {
        if (!newEventType.trim()) return;
        const name = newEventType.trim();
        const exists = eventTypes.find(e => (typeof e === 'string' ? e : e.name).toLowerCase() === name.toLowerCase());
        if (exists) {
            setToast(t('eventTypeExists') || 'Event type already exists', 'error');
            return;
        }
        const updated = [...eventTypes, { name, event_type_id: null }];
        setEventTypes(updated);
        await saveEventTypes(updated);
        setNewEventType('');
    };

    const handleUpdateEventType = async (oldVal, newName) => {
        if (!newName.trim()) return;
        const updated = eventTypes.map(e => {
            const eName = typeof e === 'string' ? e : e.name;
            if (eName === oldVal) return typeof e === 'string' ? { name: newName.trim(), event_type_id: null } : { ...e, name: newName.trim() };
            return e;
        });
        setEventTypes(updated);
        await saveEventTypes(updated);
        setEditingEventType(null);
    };

    const handleDeleteEventType = (name) => {
        setConfirmModal({
            title: t('deleteEventTypeConfirm') ? t('deleteEventTypeConfirm').replace('{name}', name) : `Delete event type "${name}"?`,
            message: 'This action cannot be undone.',
            confirmText: 'Delete',
            danger: true,
            onConfirm: async () => {
                const updated = eventTypes.filter(e => (typeof e === 'string' ? e : e.name) !== name);
                setEventTypes(updated);
                await saveEventTypes(updated);
                setConfirmModal(null);
            }
        });
    };

    // Venue CRUD
    const saveVenues = async (newList) => {
        try {
            await settingsService.update('venues', { list: newList });
            window.dispatchEvent(new CustomEvent('app_settings_updated'));
        } catch (err) {
            console.error('Failed to save venues', err);
            setToast(t('failedToSaveVenues') || 'Failed to save venues to server', 'error');
        }
    };

    const handleAddVenue = async () => {
        if (!newVenueName.trim()) return;
        const id = newVenueName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        if (venues.find(v => v.name.toLowerCase() === newVenueName.trim().toLowerCase())) {
            setToast(t('venueExists') || 'Venue already exists', 'error');
            return;
        }
        // If id collision, append a suffix
        const finalId = venues.find(v => v.id === id) ? `${id}_${Date.now()}` : id;
        const newVenue = { id: finalId, name: newVenueName.trim(), isLive: true, venue_id: newVenueId.trim() ? Number(newVenueId.trim()) : null };
        const updated = [...venues, newVenue];
        setVenues(updated);
        await saveVenues(updated);
        setNewVenueName('');
        setNewVenueId('');
    };

    const handleDeleteVenue = (id) => {
        const venue = venues.find(v => v.id === id);
        setConfirmModal({
            title: t('deleteVenueConfirm') || 'Delete this venue?',
            message: venue ? `"${venue.name}" will be permanently removed.` : 'This action cannot be undone.',
            confirmText: 'Delete',
            danger: true,
            onConfirm: async () => {
                const updated = venues.filter(v => v.id !== id);
                setVenues(updated);
                await saveVenues(updated);
                setConfirmModal(null);
            }
        });
    };

    const handleToggleVenueLive = async (id) => {
        const updated = venues.map(v => v.id === id ? { ...v, isLive: !v.isLive } : v);
        setVenues(updated);
        await saveVenues(updated);
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // BM Operations batch mode disabled
            if (false) {
                // Perform client validations
                if (batchTeamLeads.some(t => !t.name.trim() || !t.email.trim()) || batchAssociates.some(a => !a.name.trim() || !a.email.trim())) {
                    throw new Error('Please fill in all name and email fields for batch creation.');
                }

                const result = await userService.invite({
                    batch: true,
                    teamLeads: batchTeamLeads,
                    associates: batchAssociates.map(a => ({
                        name: a.name,
                        email: a.email,
                        teamLeadIndex: a.existingTeamPrequalificationId ? undefined : parseInt(a.teamLeadIndex),
                        teamLeadId: a.existingTeamPrequalificationId ? parseInt(a.existingTeamPrequalificationId) : undefined
                    }))
                });

                if (result.status) {
                    const createdTLs = result.data.teamLeads || [];
                    const createdAssocs = result.data.associates || [];
                    const allCreated = [...createdTLs.map(c => ({ ...c, role: 'Team Lead' })), ...createdAssocs.map(c => ({ ...c, role: 'Associate' }))];

                    setStatusModal({
                        type: 'success',
                        title: t('batchUsersCreated') || 'Batch Users Created Successfully',
                        message: `${createdTLs.length} Team Lead(s) and ${createdAssocs.length} Associate(s) have been added.`,
                        createdUsers: allCreated,
                        link: 'https://crmaaafm.inplsoftwares.com',
                        details: t('shareCredentials') || 'Please share these credentials with the users.'
                    });

                    // Reset form fields
                    setBatchTeamLeads([{ name: '', email: '' }]);
                    setBatchAssociates([{ name: '', email: '', teamLeadIndex: 0, existingTeamPrequalificationId: '' }]);
                    loadUsers();
                    window.dispatchEvent(new CustomEvent('users_updated'));
                } else {
                    throw new Error(result.message || 'Failed to create users');
                }
            } else {
                // CEO single mode
                if (!newUserEmail.trim() || !newUserName.trim()) return;

                const result = await userService.invite({
                    email: newUserEmail,
                    name: newUserName,
                    role: newUserRole,
                    parentId: newUserParentId || undefined
                });

                if (result.status) {
                    setShowInviteModal(false);
                    const { tempPassword } = result.data;
                    setStatusModal({
                        type: 'success',
                        title: t('userCreatedSuccessfully') || 'User Created Successfully',
                        message: (t('userAddedToTeam') || 'User {name} has been added to the team.').replace('{name}', newUserName),
                        password: tempPassword,
                        link: 'https://crmaaafm.inplsoftwares.com',
                        details: t('shareCredentials') || 'Please share these credentials with the user.'
                    });

                    setNewUserEmail('');
                    setNewUserName('');
                    setNewUserParentId('');
                    loadUsers();
                    window.dispatchEvent(new CustomEvent('users_updated'));
                } else {
                    throw new Error(result.message || 'Failed to invite user');
                }
            }
        } catch (error) {
            console.error(error);

            let errorMessage = error.message || 'An unknown error occurred.';
            let errorDetails = 'Please check the details and try again.';

            if (errorMessage.includes('Unique constraint')) {
                errorMessage = t('emailAlreadyExists') || 'A user with this email already exists.';
            } else if (errorMessage.includes('argument `role`')) {
                errorMessage = t('invalidRoleSelected') || 'Invalid role selected.';
                errorDetails = t('roleNotSupported') || 'The selected role is not supported by the system.';
            }

            setStatusModal({
                type: 'error',
                title: t('failedToInviteUser') || 'Failed to invite user',
                message: errorMessage,
                details: errorDetails
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveUser = (user) => {
        setConfirmDelete({ id: user.id, name: user.username || user.email });
    };

    const confirmDeleteAction = async () => {
        if (!confirmDelete) return;
        try {
            await userService.delete(confirmDelete.id);
            loadUsers();
            window.dispatchEvent(new CustomEvent('users_updated'));
            setConfirmDelete(null);
        } catch (error) {
            console.error(error);
            setConfirmDelete(null);
            setStatusModal({
                type: 'error',
                title: t('deletionFailed') || 'Deletion Failed',
                message: error.message || (t('failedToRemoveUser') || 'Failed to remove user'),
                details: t('tryAgainLater') || 'Please try again later.'
            });
        }
    };

    // Reset password ΓÇö generates new temp password and returns it
    const handleResetPassword = async (user) => {
        setPasswordLoading(true);
        try {
            const result = await userService.resetPassword(user.id);
            setRevealedPassword({ userId: user.id, password: result.data.tempPassword });
            setPasswordModal({ userId: user.id, userName: user.username || user.email, mode: 'view' });
        } catch (error) {
            console.error(error);
            setToast('Failed to reset password: ' + error.message, 'error');
        } finally {
            setPasswordLoading(false);
        }
    };

    // Change password to a custom one
    const handleChangePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            setToast('Password must be at least 6 characters', 'error');
            return;
        }
        setPasswordLoading(true);
        try {
            await userService.changePassword(passwordModal.userId, newPassword);
            setRevealedPassword({ userId: passwordModal.userId, password: newPassword });
            setPasswordModal(prev => ({ ...prev, mode: 'view' }));
            setNewPassword('');
            setToast('Password changed successfully', 'success');
        } catch (error) {
            console.error(error);
            setToast('Failed to change password: ' + error.message, 'error');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleUpdateUserRole = async () => {
        if (!editUserModal) return;
        setEditUserLoading(true);
        try {
            await userService.update({ id: editUserModal.id, username: editUserModal.username, role: editUserModal.role, parentId: editUserModal.parentId || null });
            setToast('User role updated successfully', 'success');
            setEditUserModal(null);
            await loadUsers();
            window.dispatchEvent(new CustomEvent('users_updated'));
        } catch (error) {
            console.error(error);
            setToast('Failed to update user role: ' + error.message, 'error');
        } finally {
            setEditUserLoading(false);
        }
    };

    // Toggle user active/inactive
    const handleToggleActive = async (user) => {
        setTogglingUser(user.id);
        try {
            const result = await userService.toggleActive(user.id, !user.is_active);
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: result.data.is_active } : u));
            window.dispatchEvent(new CustomEvent('users_updated'));
            setToast(`User ${result.data.is_active ? 'activated' : 'deactivated'} successfully`, 'success');
        } catch (error) {
            console.error(error);
            setToast('Failed to update user status: ' + error.message, 'error');
        } finally {
            setTogglingUser(null);
        }
    };

    const handleResetDatabase = () => {
        setConfirmModal({
            title: 'ΓÜá∩╕Å DANGER: Reset Database',
            message: t('resetDatabaseDanger') || 'This will permanently delete ALL prequalifications. This action CANNOT be undone. Are you absolutely sure?',
            confirmText: 'Yes, Delete All',
            danger: true,
            onConfirm: async () => {
                setConfirmModal(null);
                setResetting(true);
                try {
                    await maintenanceService.resetDatabase();
                    setToast(t('databaseClearedSuccess') || 'Database cleared successfully.', 'success');
                    setTimeout(() => window.location.reload(), 1500);
                } catch (err) {
                    console.error(err);
                    setToast((t('failedToResetDatabase') || 'Failed to reset database: {error}').replace('{error}', err.message), 'error');
                } finally {
                    setResetting(false);
                }
            }
        });
    };

    const handleMigrateContacts = () => {
        setConfirmModal({
            title: 'Migrate Contacts',
            message: t('migrateContactsConfirm2') || 'This will ensure all prequalifications have linked contacts. Continue?',
            confirmText: 'Yes, Migrate',
            danger: false,
            onConfirm: async () => {
                setConfirmModal(null);
                setLoading(true);
                try {
                    const result = await maintenanceService.migrateContacts();
                    setToast(result.message, 'success');
                    setTimeout(() => window.location.reload(), 1500);
                } catch (e) {
                    console.error(e);
                    setToast((t('migrationFailed') || 'Migration failed: {error}').replace('{error}', e.message), 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleConsolidateSources = async () => {
        setLoading(true);
        try {
            const result = await maintenanceService.consolidateSources();
            setToast(result.message, 'success');
        } catch (e) {
            console.error(e);
            setToast((t('consolidationFailed') || 'Consolidation failed: {error}').replace('{error}', e.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4 animate-fade-in relative">

            {/* Toast Banner */}
            {toast && (
                <div className={`fixed bottom-5 right-5 z-[9999] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all animate-fade-in ${toast.type === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-green-600 text-white'
                    }`}>
                    {toast.type === 'error' ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <CheckCircle className="w-4 h-4 shrink-0" />}
                    <span>{toast.msg}</span>
                </div>
            )}

            {statusModal && (
                <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className={`bg-white rounded-2xl shadow-2xl border w-full max-w-md overflow-hidden ${statusModal.type === 'error' ? 'border-red-100' : 'border-green-100'}`}>
                        <div className={`p-6 text-center ${statusModal.type === 'error' ? 'bg-red-50' : 'bg-green-50'}`}>
                            <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 ${statusModal.type === 'error' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                                {statusModal.type === 'error' ? <AlertTriangle className="w-8 h-8" /> : <CheckCircle className="w-8 h-8" />}
                            </div>
                            <h3 className={`text-xl font-bold mb-2 ${statusModal.type === 'error' ? 'text-red-900' : 'text-green-900'}`}>{statusModal.title}</h3>
                            <p className={`text-sm ${statusModal.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>{statusModal.message}</p>
                        </div>

                        <div className="p-6 space-y-4">
                            {statusModal.type === 'success' && !statusModal.createdUsers && (
                                <div className="space-y-4">
                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                        <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">{t('temporaryPassword') || 'Temporary Password'}</label>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 font-mono text-lg font-bold text-slate-800 bg-white px-3 py-2 rounded border border-slate-200">{statusModal.password}</code>
                                            <CopyButton text={statusModal.password} />
                                        </div>
                                    </div>

                                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                        <div className="flex gap-3">
                                            <div className="bg-blue-100 p-2 rounded-lg h-fit text-blue-600">
                                                <Globe className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-blue-900 text-sm mb-1">{t('loginUrl') || 'Login URL'}</h4>
                                                <a
                                                    href={statusModal.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 text-xs break-all flex items-center gap-1 font-medium"
                                                >
                                                    {statusModal.link} <ExternalLink className="w-3 h-3" />
                                                </a>
                                                <p className="text-xs text-blue-700/80 mt-2 leading-relaxed">
                                                    {t('loginInstructions') || 'Instructions: Go to the link above, login with the temporary password, and change it immediately.'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {statusModal.type === 'success' && statusModal.createdUsers && (
                                <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                                    <p className="text-xs text-gray-500 font-semibold">{t('shareCredentials') || 'Please share these credentials with the users.'}</p>
                                    <div className="divide-y divide-gray-100 border rounded-xl overflow-hidden bg-slate-50 border-slate-200">
                                        {statusModal.createdUsers.map((user, i) => (
                                            <div key={i} className="p-3 flex items-center justify-between gap-4 bg-white border-b last:border-b-0 hover:bg-slate-50 transition-colors">
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-gray-900 truncate">{user.username}</p>
                                                    <p className="text-[10px] text-gray-500 truncate">{user.email} • {user.role?.replace('_', ' ')}</p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <code className="text-xs font-mono font-bold bg-slate-100 px-2 py-1 rounded text-slate-800 border">{user.tempPassword}</code>
                                                    <CopyButton text={`Email: ${user.email}\nPassword: ${user.tempPassword}`} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="bg-blue-50 p-3 rounded-xl border border-blue-100 text-xs text-blue-700 leading-relaxed flex gap-2 items-center">
                                        <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                        <span>Login URL: <a href={statusModal.link} className="underline font-bold text-blue-800" target="_blank" rel="noreferrer">{statusModal.link}</a></span>
                                    </div>
                                </div>
                            )}

                            {statusModal.type === 'error' && (
                                <div className="bg-red-50 p-4 rounded-xl border border-red-100 text-sm text-red-800">
                                    {statusModal.details}
                                </div>
                            )}

                            <button
                                onClick={() => setStatusModal(null)}
                                className={`w-full py-3 rounded-xl font-bold transition-all transform active:scale-95 ${statusModal.type === 'error' ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-200' : 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-200'}`}
                            >
                                {statusModal.type === 'error' ? t('close') || 'Close' : 'Done'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {confirmDelete && (
                <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl border border-red-100 w-full max-w-sm overflow-hidden">
                        <div className="p-6 text-center bg-red-50">
                            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 bg-red-100 text-red-600">
                                <Trash2 className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-bold mb-2 text-red-900">{t('revokeAccessQuestion') || 'Revoke Access?'}</h3>
                            <p className="text-sm text-red-700" dangerouslySetInnerHTML={{ __html: (t('revokeAccessConfirm') || 'Are you sure you want to remove <strong>{name}</strong>? This action cannot be undone.').replace('{name}', confirmDelete.name) }}>
                            </p>
                        </div>
                        <div className="p-4 flex gap-3 bg-white">
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="flex-1 py-2.5 rounded-lg font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={confirmDeleteAction}
                                className="flex-1 py-2.5 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-200 transition-all active:scale-95"
                            >
                                {t('confirmRevoke') || 'Confirm Revoke'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Generic Confirm Modal */}
            {confirmModal && (
                <ConfirmModal
                    isOpen={!!confirmModal}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmText={confirmModal.confirmText}
                    danger={confirmModal.danger}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}

            {/* Page Header */}
            <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 border-b border-gray-100 pb-4">
                <div>
                    <h2 className={`text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${activeSection === 'settings' ? 'from-emerald-600 to-teal-600' : 'from-purple-600 to-indigo-600'}`}>
                        {activeSection === 'settings' ? t('appSettings') || 'App Settings' : t('adminControlPanel')}
                    </h2>
                    <p className="text-gray-500 mt-1">
                        {activeSection === 'users' ? t('manageTeamAccess') :
                         activeSection === 'assignment' ? t('leadRoutingLogic') || 'Prequalification routing logic' :
                         activeSection === 'automation' ? t('perSourceTriggers') || 'Per-source triggers' :
                         activeSection === 'settings' ? t('configureFeatures') || 'Configure application settings' :
                         t('manageTeamAccess')}
                    </p>
                </div>
                {activeSection === 'users' && (
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowCsvSetup(true)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-sm font-bold shadow-sm transition-all flex-shrink-0"
                        >
                            <Upload className="w-4 h-4" />
                            {t('importCsv') || 'Import CSV'}
                        </button>
                        <button
                            onClick={() => {
                                const isLead = ['Team_Lead', 'Senior_Team_Lead'].includes(activeUser?.role);
                                setInviteTab(isLead ? 'associate_team_lead' : 'team_lead');
                                setNewUserRole(isLead ? 'Associate_Team_Lead' : 'Team_Lead');
                                setShowInviteModal(true);
                            }}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-sm font-bold shadow-md hover:shadow-lg transition-all transform active:scale-95 flex-shrink-0"
                        >
                            <UserPlus className="w-4 h-4" />
                            {t('inviteTeamMember') || 'Invite Team Member'}
                        </button>
                    </div>
                )}
            </div>

            {/* Assignment Rules Section */}
            {activeSection === 'assignment' && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl">
                    <div className="w-full p-5 flex items-center justify-between text-left border-b border-purple-200 mb-4">
                        <div className="flex items-center gap-2">
                            <Shuffle className="w-4 h-4 text-purple-600" />
                            <h3 className="text-sm font-bold text-purple-800">{t('assignmentRules')}</h3>
                        </div>
                    </div>

                    <div className="px-5 pb-5 space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-purple-700 mb-2">{t('assignmentMode')}</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {[
                                    { value: 'round_robin', label: t('roundRobin'), desc: t('roundRobinDesc') },
                                    { value: 'source_based', label: t('bySource'), desc: t('bySourceDesc') },
                                    { value: 'single_person', label: t('singlePerson'), desc: t('singlePersonDesc') },
                                    { value: 'manual', label: t('manualOnly'), desc: t('manualOnlyDesc') }
                                ].map((opt) => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setAssignmentConfig((prev) => ({ ...prev, mode: opt.value }))}
                                        className={`p-3 rounded-lg border text-left transition-all ${assignmentConfig.mode === opt.value
                                            ? 'border-purple-500 bg-purple-100'
                                            : 'border-gray-200 bg-white hover:border-purple-300'
                                            }`}
                                    >
                                        <div className="font-medium text-sm">{opt.label}</div>
                                        <div className="text-xs text-gray-500">{opt.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {assignmentConfig.mode === 'single_person' && (
                            <div>
                                <label className="block text-xs font-bold text-purple-700 mb-2">{t('assignAllLeadsTo')}</label>
                                <Dropdown>
                                    <DropdownTrigger>
                                        <button type="button" className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                            {assignmentConfig.defaultAssignee ? users.find(u => u.username === assignmentConfig.defaultAssignee)?.username || users.find(u => u.email === assignmentConfig.defaultAssignee)?.email || assignmentConfig.defaultAssignee : 'Select User...'}
                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                        </button>
                                    </DropdownTrigger>
                                    <DropdownPopover className="w-full">
                                        <DropdownMenu
                                            selectedKeys={new Set([assignmentConfig.defaultAssignee || ''])}
                                            selectionMode="single"
                                            onSelectionChange={(keys) => setAssignmentConfig((prev) => ({ ...prev, defaultAssignee: Array.from(keys)[0] }))}
                                        >
                                            <DropdownItem id="">Select User...</DropdownItem>
                                            {users.map((u) => (
                                                <DropdownItem key={u.id} id={u.username || u.email}>{u.username || u.email}</DropdownItem>
                                            ))}
                                        </DropdownMenu>
                                    </DropdownPopover>
                                </Dropdown>
                            </div>
                        )}

                        {assignmentConfig.mode === 'source_based' && (
                            <div>
                                <label className="block text-xs font-bold text-purple-700 mb-2">{t('sourceEmployeeRules')}</label>
                                <div className="space-y-3">
                                    {(assignmentConfig.sourceRules || []).map((rule, idx) => (
                                        <div key={idx} className="p-4 bg-white rounded-xl border border-gray-200 space-y-3">
                                            {/* Row 1: Source + Assignment Type */}
                                            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                                                {/* Source Dropdown */}
                                                <div className="flex-1 min-w-0">
                                                    <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">{t('sourceLabel') || 'Source'}</label>
                                                    <Dropdown>
                                                        <DropdownTrigger>
                                                            <button type="button" className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                                {rule.source || t('selectSource') || 'Select Source...'}
                                                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                            </button>
                                                        </DropdownTrigger>
                                                        <DropdownPopover className="w-full">
                                                            <DropdownMenu
                                                                selectedKeys={new Set([rule.source])}
                                                                selectionMode="single"
                                                                onSelectionChange={(keys) => updateSourceRule(idx, 'source', Array.from(keys)[0])}
                                                            >
                                                                <DropdownItem id="">{t('selectSource') || 'Select Source...'}</DropdownItem>
                                                                {sources.map((s) => (
                                                                    <DropdownItem key={s.id} id={s.name}>{s.name}</DropdownItem>
                                                                ))}
                                                            </DropdownMenu>
                                                        </DropdownPopover>
                                                    </Dropdown>
                                                </div>
                                                <span className="text-gray-400 hidden sm:block pt-5">ΓåÆ</span>
                                                {/* Assignment Type Dropdown */}
                                                <div className="flex-1 min-w-0">
                                                    <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">{t('assignmentTypeLabel') || 'Assignment Type'}</label>
                                                    <Dropdown>
                                                        <DropdownTrigger>
                                                            <button type="button" className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                                {(rule.assignmentType || 'specific_user') === 'round_robin_role' ? <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-blue-500" /> {t('roundRobinSales') || 'Round Robin (Sales)'}</div>
                                                                    : (rule.assignmentType === 'round_robin_all') ? <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-purple-500" /> {t('roundRobinAll') || 'Round Robin (All Users)'}</div>
                                                                        : <div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-500" /> {t('specificUser') || 'Specific User'}</div>}
                                                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                            </button>
                                                        </DropdownTrigger>
                                                        <DropdownPopover className="w-full">
                                                            <DropdownMenu
                                                                selectedKeys={new Set([rule.assignmentType || 'specific_user'])}
                                                                selectionMode="single"
                                                                onSelectionChange={(keys) => {
                                                                    const type = Array.from(keys)[0];
                                                                    setAssignmentConfig((prev) => {
                                                                        const rules = [...(prev.sourceRules || [])];
                                                                        rules[idx] = {
                                                                            ...rules[idx],
                                                                            assignmentType: type,
                                                                            role: type === 'round_robin_role' ? (rules[idx].role || 'Sales') : null,
                                                                            assignTo: type === 'specific_user' ? (rules[idx].assignTo || '') : null
                                                                        };
                                                                        return { ...prev, sourceRules: rules };
                                                                    });
                                                                }}
                                                            >
                                                                <DropdownItem id="round_robin_role"><div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-blue-500" /> {t('roundRobinSales') || 'Round Robin (Sales)'}</div></DropdownItem>
                                                                <DropdownItem id="round_robin_all"><div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-purple-500" /> {t('roundRobinAll') || 'Round Robin (All Users)'}</div></DropdownItem>
                                                                <DropdownItem id="specific_user"><div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-500" /> {t('specificUser') || 'Specific User'}</div></DropdownItem>
                                                            </DropdownMenu>
                                                        </DropdownPopover>
                                                    </Dropdown>
                                                </div>
                                                <button
                                                    onClick={() => removeSourceRule(idx)}
                                                    className="self-end sm:self-auto sm:pt-5 p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* Row 2: Conditional ΓÇö Specific User selector */}
                                            {(rule.assignmentType || 'specific_user') === 'specific_user' && (
                                                <div className="pl-0 sm:pl-2">
                                                    <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">{t('assignToLabel') || 'Assign To'}</label>
                                                    <Dropdown>
                                                        <DropdownTrigger>
                                                            <button type="button" className="w-full sm:w-1/2 px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                                {rule.assignTo ? users.find(u => u.username === rule.assignTo)?.username || users.find(u => u.email === rule.assignTo)?.email || rule.assignTo : t('selectUser') || 'Select User...'}
                                                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                            </button>
                                                        </DropdownTrigger>
                                                        <DropdownPopover className="w-full">
                                                            <DropdownMenu
                                                                selectedKeys={new Set([rule.assignTo || ''])}
                                                                selectionMode="single"
                                                                onSelectionChange={(keys) => updateSourceRule(idx, 'assignTo', Array.from(keys)[0])}
                                                            >
                                                                <DropdownItem id="">{t('selectUser') || 'Select User...'}</DropdownItem>
                                                                {users.map((u) => (
                                                                    <DropdownItem key={u.id} id={u.username || u.email}>{u.username || u.email}</DropdownItem>
                                                                ))}
                                                            </DropdownMenu>
                                                        </DropdownPopover>
                                                    </Dropdown>
                                                </div>
                                            )}

                                            {/* Info badge for round robin modes */}
                                            {(rule.assignmentType === 'round_robin_role') && (
                                                <p className="text-xs text-purple-600 bg-purple-50 px-3 py-1.5 rounded-lg flex items-start gap-1.5">
                                                    <RefreshCw className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{t('roundRobinSalesDesc') || 'Prequalifications will be distributed among all active Sales users in round-robin order'}</span>
                                                </p>
                                            )}
                                            {(rule.assignmentType === 'round_robin_all') && (
                                                <p className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg flex items-start gap-1.5">
                                                    <RefreshCw className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{t('roundRobinAllDesc') || 'Prequalifications will be distributed among all active users in round-robin order'}</span>
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        onClick={addSourceRule}
                                        className="text-purple-600 text-sm font-medium flex items-center gap-1 hover:text-purple-700"
                                    >
                                        <Plus className="w-4 h-4" /> {t('addRule') || 'Add Rule'}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">{t('noSourceRulesDesc') || 'Prequalifications from sources not listed will use round-robin.'}</p>
                            </div>
                        )}

                        {assignmentConfig.mode !== 'manual' && (
                            <div>
                                <label className="block text-xs font-bold text-purple-700 mb-2">{t('fallbackAssignee')}</label>
                                <Dropdown>
                                    <DropdownTrigger>
                                        <button type="button" className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                            {(assignmentConfig.fallbackAssignee || 'round_robin') === 'round_robin' ? t('useRoundRobin') :
                                                (assignmentConfig.fallbackAssignee === 'unassigned') ? t('leaveUnassigned') :
                                                    t('alwaysAssignTo').replace('{name}', users.find(u => u.username === assignmentConfig.fallbackAssignee)?.username || users.find(u => u.email === assignmentConfig.fallbackAssignee)?.email || assignmentConfig.fallbackAssignee)}
                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                        </button>
                                    </DropdownTrigger>
                                    <DropdownPopover className="w-full">
                                        <DropdownMenu
                                            selectedKeys={new Set([assignmentConfig.fallbackAssignee || 'round_robin'])}
                                            selectionMode="single"
                                            onSelectionChange={(keys) => setAssignmentConfig((prev) => ({ ...prev, fallbackAssignee: Array.from(keys)[0] }))}
                                        >
                                            <DropdownItem id="round_robin">{t('useRoundRobin')}</DropdownItem>
                                            <DropdownItem id="unassigned">{t('leaveUnassigned')}</DropdownItem>
                                            {users.map((u) => (
                                                <DropdownItem key={u.id} id={u.username || u.email}>
                                                    {t('alwaysAssignTo').replace('{name}', u.username || u.email)}
                                                </DropdownItem>
                                            ))}
                                        </DropdownMenu>
                                    </DropdownPopover>
                                </Dropdown>
                            </div>
                        )}

                        <div className="flex justify-center pt-2">
                            <button
                                onClick={handleSaveAssignmentRules}
                                disabled={loading}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                            >
                                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                                {t('saveAssignmentRules')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Automation Rules Section */}
            {activeSection === 'automation' && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl overflow-hidden">
                    <div className="w-full p-5 flex items-center justify-between text-left border-b border-cyan-200 mb-4">
                        <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-cyan-600" />
                            <h3 className="text-sm font-bold text-cyan-800">{t('leadAutomationRules') || 'Prequalification Automation Rules'}</h3>
                            <span className="text-xs text-cyan-600 bg-cyan-100 px-2 py-0.5 rounded-full">{t('perSource')}</span>
                        </div>
                    </div>

                    <div className="px-5 pb-5 space-y-4">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder={t('searchSources')}
                                value={automationSearch}
                                onChange={(e) => setAutomationSearch(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
                            />
                        </div>

                        <div className="bg-white rounded-lg border overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs table-fixed">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            <th className="w-[40%] px-2 sm:px-4 py-3 text-left font-semibold text-gray-600">{t('source')}</th>
                                            <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                <div className="flex flex-col items-center gap-1">
                                                    <PhoneCall className="w-4 h-4 text-blue-500" />
                                                    <span className="hidden sm:inline">{t('callListTh') || 'Follow up List'}</span>
                                                </div>
                                            </th>
                                            <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                <div className="flex flex-col items-center gap-1">
                                                    <Bell className="w-4 h-4 text-amber-500" />
                                                    <span className="hidden sm:inline">{t('notify')}</span>
                                                </div>
                                            </th>
                                            <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                <div className="flex flex-col items-center gap-1">
                                                    <Mail className="w-4 h-4 text-green-500" />
                                                    <span className="hidden sm:inline">{t('email')}</span>
                                                    <span className="text-[8px] text-gray-400 leading-none">soon</span>
                                                </div>
                                            </th>
                                            <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                <div className="flex flex-col items-center gap-1">
                                                    <Globe className="w-4 h-4 text-orange-500" />
                                                    <span className="hidden sm:inline text-center leading-tight">{t('pushNotif') || 'Push'}</span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        <tr className="bg-blue-50/50">
                                            <td className="px-2 sm:px-4 py-2.5">
                                                <div>
                                                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">{t('default')}</span>
                                                    <div className="text-gray-400 text-[9px] mt-0.5">{t('allSources') || 'All Sources'}</div>
                                                </div>
                                            </td>
                                            {['addToCallList', 'sendNotification', 'emailResponse', 'browserNotification'].map(field => {
                                                const isEmailField = field === 'emailResponse';
                                                return (
                                                <td key={field} className="px-1 py-2.5 text-center">
                                                    <button
                                                        onClick={() => !isEmailField && toggleAutomation('_default', field)}
                                                        title={isEmailField ? 'Email integration coming soon' : ''}
                                                        className={`w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 relative ${
                                                            isEmailField
                                                                ? 'bg-gray-200 cursor-not-allowed opacity-50'
                                                                : getAutomationRule('_default')[field] ? 'bg-blue-500' : 'bg-gray-300'
                                                        }`}
                                                        style={{ minWidth: '36px' }}
                                                    >
                                                        <span
                                                            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                                                !isEmailField && getAutomationRule('_default')[field] ? 'translate-x-4' : 'translate-x-0'
                                                            }`}
                                                        />
                                                    </button>
                                                </td>
                                                );
                                            })}
                                        </tr>

                                        {initialLoading ? (
                                            [1, 2, 3, 4, 5, 6].map(i => (
                                                <tr key={i} className="hover:bg-gray-50">
                                                    <td className="px-2 sm:px-4 py-2.5 border-r border-gray-100">
                                                        <Skeleton width={80} height={14} animation="wave" />
                                                    </td>
                                                    {[1, 2, 3, 4].map(j => (
                                                        <td key={j} className="px-1 py-2.5 text-center">
                                                            <Skeleton variant="rounded" width={36} height={20} animation="wave" className="rounded-full mx-auto" />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            sources
                                                .filter(s => !automationSearch || s.name.toLowerCase().includes(automationSearch.toLowerCase()))
                                                .map(source => {
                                                    const sourceKey = source.name.replace(/\s+/g, '_').toLowerCase();
                                                    return (
                                                        <tr key={source.id} className="hover:bg-gray-50">
                                                            <td className="px-2 sm:px-4 py-2.5">
                                                                <div>
                                                                    <span className="font-medium text-gray-800 text-[10px] sm:text-xs leading-tight block">{source.name}</span>
                                                                    {source.isIntegration && (
                                                                        <span className="px-1 py-0.5 bg-purple-100 text-purple-600 text-[8px] rounded">{t('integrationBadge') || 'Int.'}</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            {['addToCallList', 'sendNotification', 'emailResponse', 'browserNotification'].map(field => {
                                                                const isEmailField = field === 'emailResponse';
                                                                return (
                                                                <td key={field} className="px-1 py-2.5 text-center">
                                                                    <button
                                                                        onClick={() => !isEmailField && toggleAutomation(sourceKey, field)}
                                                                        title={isEmailField ? 'Email integration coming soon' : ''}
                                                                        className={`w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 relative ${
                                                                            isEmailField
                                                                                ? 'bg-gray-200 cursor-not-allowed opacity-50'
                                                                                : getAutomationRule(sourceKey)[field] ? 'bg-blue-500' : 'bg-gray-300'
                                                                        }`}
                                                                        style={{ minWidth: '36px' }}
                                                                    >
                                                                        <span
                                                                            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                                                                !isEmailField && getAutomationRule(sourceKey)[field] ? 'translate-x-4' : 'translate-x-0'
                                                                            }`}
                                                                        />
                                                                    </button>
                                                                </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1"><PhoneCall className="w-3 h-3 text-blue-500" /><span>{t('callListAutoAdd')}</span></div>
                            <div className="flex items-center gap-1"><Bell className="w-3 h-3 text-amber-500" /><span>{t('notifyDesc')}</span></div>
                            <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-green-500" /><span>{t('emailDesc')} <span className="text-gray-400">(coming soon)</span></span></div>
                            <div className="flex items-center gap-1"><Globe className="w-3 h-3 text-orange-500" /><span>{t('browserNotificationsDesc') || 'Push - FCM browser/device push notifications'}</span></div>
                        </div>

                    </div>

                    {/* Tender / Proposal Automation Rules */}
                    <div className="bg-white rounded-xl border border-teal-200 overflow-hidden mt-4">
                        <div className="w-full p-4 flex items-center gap-2 border-b border-teal-100 bg-teal-50">
                            <Briefcase className="w-4 h-4 text-teal-600" />
                            <h3 className="text-sm font-bold text-teal-800">{t('tenderAutomationRules') || 'Tender / Proposal Automation Rules'}</h3>
                            <span className="text-xs text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full">{t('perSource')}</span>
                        </div>

                        <div className="px-5 pb-5 pt-4 space-y-4">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={t('searchSources')}
                                    value={tenderAutomationSearch}
                                    onChange={(e) => setTenderAutomationSearch(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
                                />
                            </div>

                            <div className="bg-white rounded-lg border overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs table-fixed">
                                        <thead className="bg-gray-50 border-b">
                                            <tr>
                                                <th className="w-[40%] px-2 sm:px-4 py-3 text-left font-semibold text-gray-600">{t('source')}</th>
                                                <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <PhoneCall className="w-4 h-4 text-blue-500" />
                                                        <span className="hidden sm:inline">{t('callListTh') || 'Follow up List'}</span>
                                                    </div>
                                                </th>
                                                <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <Bell className="w-4 h-4 text-amber-500" />
                                                        <span className="hidden sm:inline">{t('notify')}</span>
                                                    </div>
                                                </th>
                                                <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <Mail className="w-4 h-4 text-green-500" />
                                                        <span className="hidden sm:inline">{t('email')}</span>
                                                        <span className="text-[8px] text-gray-400 leading-none">soon</span>
                                                    </div>
                                                </th>
                                                <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <Globe className="w-4 h-4 text-orange-500" />
                                                        <span className="hidden sm:inline text-center leading-tight">{t('pushNotif') || 'Push'}</span>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            <tr className="bg-blue-50/50">
                                                <td className="px-2 sm:px-4 py-2.5">
                                                    <div>
                                                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">{t('default')}</span>
                                                        <div className="text-gray-400 text-[9px] mt-0.5">{t('allSources') || 'All Sources'}</div>
                                                    </div>
                                                </td>
                                                {['addToCallList', 'sendNotification', 'emailResponse', 'browserNotification'].map(field => {
                                                    const isEmail = field === 'emailResponse';
                                                    return (
                                                    <td key={field} className="px-1 py-2.5 text-center">
                                                        <button
                                                            onClick={() => !isEmail && toggleTenderAutomation('_default', field)}
                                                            title={isEmail ? 'Email integration coming soon' : ''}
                                                            className={`w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 relative ${
                                                                isEmail
                                                                    ? 'bg-gray-200 cursor-not-allowed opacity-50'
                                                                    : getTenderAutomationRule('_default')[field] ? 'bg-teal-500' : 'bg-gray-300'
                                                            }`}
                                                            style={{ minWidth: '36px' }}
                                                        >
                                                            <span
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                                                    !isEmail && getTenderAutomationRule('_default')[field] ? 'translate-x-4' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </button>
                                                    </td>
                                                    );
                                                })}
                                            </tr>
                                            {initialLoading ? (
                                                [1, 2, 3].map(i => (
                                                    <tr key={i} className="hover:bg-gray-50">
                                                        <td className="px-2 sm:px-4 py-2.5 border-r border-gray-100">
                                                            <Skeleton width={80} height={14} animation="wave" />
                                                        </td>
                                                        {[1, 2, 3, 4].map(j => (
                                                            <td key={j} className="px-1 py-2.5 text-center">
                                                                <Skeleton variant="rounded" width={36} height={20} animation="wave" className="rounded-full mx-auto" />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                sources
                                                    .filter(s => !tenderAutomationSearch || s.name.toLowerCase().includes(tenderAutomationSearch.toLowerCase()))
                                                    .map(source => {
                                                        const sourceKey = source.name.replace(/\s+/g, '_').toLowerCase();
                                                        return (
                                                            <tr key={source.id} className="hover:bg-gray-50">
                                                                <td className="px-2 sm:px-4 py-2.5">
                                                                    <div>
                                                                        <span className="font-medium text-gray-800 text-[10px] sm:text-xs leading-tight block">{source.name}</span>
                                                                        {source.isIntegration && (
                                                                            <span className="px-1 py-0.5 bg-purple-100 text-purple-600 text-[8px] rounded">{t('integrationBadge') || 'Int.'}</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                {['addToCallList', 'sendNotification', 'emailResponse', 'browserNotification'].map(field => {
                                                                    const isEmail = field === 'emailResponse';
                                                                    return (
                                                                    <td key={field} className="px-1 py-2.5 text-center">
                                                                        <button
                                                                            onClick={() => !isEmail && toggleTenderAutomation(sourceKey, field)}
                                                                            title={isEmail ? 'Email integration coming soon' : ''}
                                                                            className={`w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 relative ${
                                                                                isEmail
                                                                                    ? 'bg-gray-200 cursor-not-allowed opacity-50'
                                                                                    : getTenderAutomationRule(sourceKey)[field] ? 'bg-teal-500' : 'bg-gray-300'
                                                                            }`}
                                                                            style={{ minWidth: '36px' }}
                                                                        >
                                                                            <span
                                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                                                                    !isEmail && getTenderAutomationRule(sourceKey)[field] ? 'translate-x-4' : 'translate-x-0'
                                                                                }`}
                                                                            />
                                                                        </button>
                                                                    </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        );
                                                    })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                                <div className="flex items-center gap-1"><PhoneCall className="w-3 h-3 text-blue-500" /><span>{t('callListAutoAdd')}</span></div>
                                <div className="flex items-center gap-1"><Bell className="w-3 h-3 text-amber-500" /><span>{t('notifyDesc')}</span></div>
                                <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-green-500" /><span>{t('emailDesc')} <span className="text-gray-400">(coming soon)</span></span></div>
                                <div className="flex items-center gap-1"><Globe className="w-3 h-3 text-orange-500" /><span>{t('pushNotifDesc') || 'Push - FCM browser/device push notifications'}</span></div>
                            </div>
                        </div>
                    </div>

                    {/* Profile Automation Rules */}
                    <div className="bg-white rounded-xl border border-indigo-200 overflow-hidden mt-4">
                        <div className="w-full p-4 flex items-center gap-2 border-b border-indigo-100 bg-indigo-50">
                            <User className="w-4 h-4 text-indigo-600" />
                            <h3 className="text-sm font-bold text-indigo-800">{t('profileAutomationRules') || 'Profile Automation Rules'}</h3>
                            <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">{t('perSource')}</span>
                        </div>

                        <div className="px-5 pb-5 pt-4 space-y-4">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={t('searchSources')}
                                    value={profileAutomationSearch}
                                    onChange={(e) => setProfileAutomationSearch(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
                                />
                            </div>

                            <div className="bg-white rounded-lg border overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs table-fixed">
                                        <thead className="bg-gray-50 border-b">
                                            <tr>
                                                <th className="w-[40%] px-2 sm:px-4 py-3 text-left font-semibold text-gray-600">{t('source')}</th>
                                                <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <PhoneCall className="w-4 h-4 text-blue-500" />
                                                        <span className="hidden sm:inline">{t('callListTh') || 'Follow up List'}</span>
                                                    </div>
                                                </th>
                                                <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <Bell className="w-4 h-4 text-amber-500" />
                                                        <span className="hidden sm:inline">{t('notify')}</span>
                                                    </div>
                                                </th>
                                                <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <Mail className="w-4 h-4 text-green-500" />
                                                        <span className="hidden sm:inline">{t('email')}</span>
                                                        <span className="text-[8px] text-gray-400 leading-none">soon</span>
                                                    </div>
                                                </th>
                                                <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <Globe className="w-4 h-4 text-orange-500" />
                                                        <span className="hidden sm:inline text-center leading-tight">{t('pushNotif') || 'Push'}</span>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            <tr className="bg-blue-50/50">
                                                <td className="px-2 sm:px-4 py-2.5">
                                                    <div>
                                                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">{t('default')}</span>
                                                        <div className="text-gray-400 text-[9px] mt-0.5">{t('allSources') || 'All Sources'}</div>
                                                    </div>
                                                </td>
                                                {['addToCallList', 'sendNotification', 'emailResponse', 'browserNotification'].map(field => {
                                                    const isEmail = field === 'emailResponse';
                                                    return (
                                                    <td key={field} className="px-1 py-2.5 text-center">
                                                        <button
                                                            onClick={() => !isEmail && toggleProfileAutomation('_default', field)}
                                                            title={isEmail ? 'Email integration coming soon' : ''}
                                                            className={`w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 relative ${
                                                                isEmail
                                                                    ? 'bg-gray-200 cursor-not-allowed opacity-50'
                                                                    : getProfileAutomationRule('_default')[field] ? 'bg-indigo-500' : 'bg-gray-300'
                                                            }`}
                                                            style={{ minWidth: '36px' }}
                                                        >
                                                            <span
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                                                    !isEmail && getProfileAutomationRule('_default')[field] ? 'translate-x-4' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </button>
                                                    </td>
                                                    );
                                                })}
                                            </tr>
                                            {initialLoading ? (
                                                [1, 2, 3].map(i => (
                                                    <tr key={i} className="hover:bg-gray-50">
                                                        <td className="px-2 sm:px-4 py-2.5 border-r border-gray-100">
                                                            <Skeleton width={80} height={14} animation="wave" />
                                                        </td>
                                                        {[1, 2, 3, 4].map(j => (
                                                            <td key={j} className="px-1 py-2.5 text-center">
                                                                <Skeleton variant="rounded" width={36} height={20} animation="wave" className="rounded-full mx-auto" />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                sources
                                                    .filter(s => !profileAutomationSearch || s.name.toLowerCase().includes(profileAutomationSearch.toLowerCase()))
                                                    .map(source => {
                                                        const sourceKey = source.name.replace(/\s+/g, '_').toLowerCase();
                                                        return (
                                                            <tr key={source.id} className="hover:bg-gray-50">
                                                                <td className="px-2 sm:px-4 py-2.5">
                                                                    <div>
                                                                        <span className="font-medium text-gray-800 text-[10px] sm:text-xs leading-tight block">{source.name}</span>
                                                                        {source.isIntegration && (
                                                                            <span className="px-1 py-0.5 bg-purple-100 text-purple-600 text-[8px] rounded">{t('integrationBadge') || 'Int.'}</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                {['addToCallList', 'sendNotification', 'emailResponse', 'browserNotification'].map(field => {
                                                                    const isEmail = field === 'emailResponse';
                                                                    return (
                                                                    <td key={field} className="px-1 py-2.5 text-center">
                                                                        <button
                                                                            onClick={() => !isEmail && toggleProfileAutomation(sourceKey, field)}
                                                                            title={isEmail ? 'Email integration coming soon' : ''}
                                                                            className={`w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 relative ${
                                                                                isEmail
                                                                                    ? 'bg-gray-200 cursor-not-allowed opacity-50'
                                                                                    : getProfileAutomationRule(sourceKey)[field] ? 'bg-indigo-500' : 'bg-gray-300'
                                                                            }`}
                                                                            style={{ minWidth: '36px' }}
                                                                        >
                                                                            <span
                                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                                                                    !isEmail && getProfileAutomationRule(sourceKey)[field] ? 'translate-x-4' : 'translate-x-0'
                                                                                }`}
                                                                            />
                                                                        </button>
                                                                    </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        );
                                                    })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                                <div className="flex items-center gap-1"><PhoneCall className="w-3 h-3 text-blue-500" /><span>{t('callListAutoAdd')}</span></div>
                                <div className="flex items-center gap-1"><Bell className="w-3 h-3 text-amber-500" /><span>{t('notifyDesc')}</span></div>
                                <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-green-500" /><span>{t('emailDesc')} <span className="text-gray-400">(coming soon)</span></span></div>
                                <div className="flex items-center gap-1"><Globe className="w-3 h-3 text-orange-500" /><span>{t('pushNotifDesc') || 'Push - FCM browser/device push notifications'}</span></div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center pt-2 pb-2">
                        <button
                            onClick={handleSaveAutomationRules}
                            disabled={loading}
                            className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                        >
                            {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                            {t('saveAutomationRules')}
                        </button>
                    </div>
                </div>
            )}

                        {/* Settings Section */}
            {activeSection === 'settings' && (
                <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                    {/* Card wrapper */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden relative">
                        {/* Tabs */}
                        <div className="flex border-b bg-gray-50 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                            {[
                                { id: 'services', label: t('services') || 'Services', icon: Briefcase },
                                { id: 'cities', label: t('cities') || 'Cities', icon: MapPin }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveSettingsTab(tab.id)}
                                    className={`flex-1 min-w-max px-4 py-4 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${activeSettingsTab === tab.id
                                        ? 'border-emerald-600 text-emerald-600 bg-white'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <tab.icon className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="p-6 flex-1 overflow-y-auto bg-slate-50 min-h-[400px]">
                            {activeSettingsTab === 'services' && (
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Briefcase className="w-4 h-4 text-emerald-600" />
                                        <h4 className="text-sm font-bold text-emerald-800">{t('services') || 'Services'}</h4>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-5 py-3">{t('serviceName') || 'Service Name'}</th>
                                                        <th className="px-5 py-3 w-32 text-right">{t('actions') || 'Actions'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {initialLoading ? (
                                                        [1, 2, 3].map((i) => (
                                                            <tr key={i} className="hover:bg-gray-50">
                                                                <td className="px-5 py-3"><Skeleton width="60%" height={16} animation="wave" /></td>
                                                                <td className="px-5 py-3 flex justify-end"><Skeleton width={60} height={24} animation="wave" className="rounded-md" /></td>
                                                            </tr>
                                                        ))
                                                    ) : services.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="2" className="text-center py-6 text-gray-500">
                                                                {t('noServicesFound') || 'No services found.'}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        services.map((service) => (
                                                            <tr key={service.id} className="hover:bg-gray-50 transition-colors">
                                                                <td className="px-5 py-3 font-medium text-gray-900 w-full">
                                                                    {editingServiceId === service.id ? (
                                                                        <input
                                                                            type="text"
                                                                            autoFocus
                                                                            value={editingServiceName}
                                                                            onChange={(e) => setEditingServiceName(e.target.value)}
                                                                            onKeyDown={async (e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    if (!editingServiceName.trim() || editingServiceName === service.name) {
                                                                                        setEditingServiceId(null);
                                                                                        return;
                                                                                    }
                                                                                    try {
                                                                                        const res = await servicesService.update(service.id, editingServiceName.trim());
                                                                                        setServices(prev => prev.map(s => s.id === service.id ? res : s));
                                                                                        setEditingServiceId(null);
                                                                                        setToast('Service updated', 'success');
                                                                                    } catch(err) {
                                                                                        setToast(err.message, 'error');
                                                                                    }
                                                                                } else if (e.key === 'Escape') {
                                                                                    setEditingServiceId(null);
                                                                                }
                                                                            }}
                                                                            className="w-full px-2 py-1 border border-emerald-500 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                                                        />
                                                                    ) : (
                                                                        service.name
                                                                    )}
                                                                </td>
                                                                <td className="px-5 py-3 flex justify-end gap-1">
                                                                    {editingServiceId === service.id ? (
                                                                        <>
                                                                            <button onClick={async () => {
                                                                                if (!editingServiceName.trim() || editingServiceName === service.name) {
                                                                                    setEditingServiceId(null);
                                                                                    return;
                                                                                }
                                                                                try {
                                                                                    const res = await servicesService.update(service.id, editingServiceName.trim());
                                                                                    setServices(prev => prev.map(s => s.id === service.id ? res : s));
                                                                                    setEditingServiceId(null);
                                                                                    setToast('Service updated', 'success');
                                                                                } catch(err) {
                                                                                    setToast(err.message, 'error');
                                                                                }
                                                                            }} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors" title="Save">
                                                                                <CheckCircle className="w-4 h-4" />
                                                                            </button>
                                                                            <button onClick={() => setEditingServiceId(null)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md transition-colors" title="Cancel">
                                                                                <X className="w-4 h-4" />
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <button onClick={() => {
                                                                                setEditingServiceId(service.id);
                                                                                setEditingServiceName(service.name);
                                                                            }} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-md transition-colors" title="Edit">
                                                                                <Edit2 className="w-4 h-4" />
                                                                            </button>
                                                                            <button onClick={() => {
                                                                                setConfirmModal({
                                                                                    title: 'Delete Service',
                                                                                    message: `Are you sure you want to delete "${service.name}"?`,
                                                                                    confirmText: 'Delete',
                                                                                    danger: true,
                                                                                    onConfirm: async () => {
                                                                                        try {
                                                                                            await servicesService.delete(service.id);
                                                                                            setServices(prev => prev.filter(s => s.id !== service.id));
                                                                                            setToast('Service deleted', 'success');
                                                                                        } catch(err) {
                                                                                            setToast(err.message, 'error');
                                                                                        } finally {
                                                                                            setConfirmModal(null);
                                                                                        }
                                                                                    }
                                                                                });
                                                                            }} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Delete">
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                            <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
                                                <input
                                                    type="text"
                                                    placeholder={t('newServicePlaceholder') || 'New service name...'}
                                                    value={newServiceName}
                                                    onChange={(e) => setNewServiceName(e.target.value)}
                                                    onKeyDown={async (e) => {
                                                        if (e.key === 'Enter' && newServiceName.trim()) {
                                                            try {
                                                                const res = await servicesService.create(newServiceName.trim());
                                                                setServices(prev => [...prev, res]);
                                                                setNewServiceName('');
                                                                setToast('Service added', 'success');
                                                            } catch(err) {
                                                                setToast(err.message, 'error');
                                                            }
                                                        }
                                                    }}
                                                    className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500 shadow-sm"
                                                />
                                                <button
                                                    onClick={async () => {
                                                        if (!newServiceName.trim()) return;
                                                        try {
                                                            const res = await servicesService.create(newServiceName.trim());
                                                            setServices(prev => [...prev, res]);
                                                            setNewServiceName('');
                                                            setToast('Service added', 'success');
                                                        } catch(err) {
                                                            setToast(err.message, 'error');
                                                        }
                                                    }}
                                                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-1 shrink-0 transition-colors shadow-sm"
                                                >
                                                    <Plus className="w-4 h-4" /> {t('add') || 'Add'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeSettingsTab === 'cities' && (
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-emerald-600" />
                                            <h4 className="text-sm font-bold text-emerald-800">{t('cities') || 'Cities'}</h4>
                                            <span className="text-xs text-gray-400 ml-1">({cities.filter(c => !citySearch || c.name.toLowerCase().includes(citySearch.toLowerCase())).length})</span>
                                        </div>
                                        <div className="relative">
                                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder={t('searchCities') || 'Search cities...'}
                                                value={citySearch}
                                                onChange={(e) => setCitySearch(e.target.value)}
                                                className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500 w-48"
                                            />
                                        </div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                        {/* Mobile Card View */}
                                        <div className="block sm:hidden p-4 space-y-2 bg-gray-50/50 max-h-[400px] overflow-y-auto">
                                            {initialLoading ? (
                                                [1, 2, 3].map(i => (
                                                    <div key={i} className="bg-white rounded-lg border border-gray-100 p-3">
                                                        <Skeleton width={150} height={18} animation="wave" />
                                                    </div>
                                                ))
                                            ) : cities.filter(c => !citySearch || c.name.toLowerCase().includes(citySearch.toLowerCase())).length === 0 ? (
                                                <div className="text-center py-6 text-gray-500 text-sm">
                                                    {t('noCitiesFound') || 'No cities found.'}
                                                </div>
                                            ) : (
                                                cities.filter(c => !citySearch || c.name.toLowerCase().includes(citySearch.toLowerCase())).map((city) => (
                                                    <div key={city.id} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${city.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                                                            <span className="text-sm font-medium text-gray-800">{city.name}</span>
                                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${city.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                                                {city.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <button
                                                                onClick={async () => {
                                                                    try {
                                                                        const updated = await citiesService.toggleStatus(city.id);
                                                                        setCities(prev => prev.map(c => c.id === city.id ? updated : c));
                                                                    } catch(err) { setToast(err.message, 'error'); }
                                                                }}
                                                                className={`relative w-9 h-5 !min-h-[20px] rounded-full transition-colors focus:outline-none flex-shrink-0 ${city.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                                                style={{ minWidth: '36px' }}
                                                            >
                                                                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${city.isActive ? 'translate-x-4' : 'translate-x-0'}`} />
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setConfirmModal({
                                                                        title: 'Delete City',
                                                                        message: `Are you sure you want to delete "${city.name}"?`,
                                                                        confirmText: 'Delete',
                                                                        danger: true,
                                                                        onConfirm: async () => {
                                                                            try {
                                                                                await citiesService.delete(city.id);
                                                                                setCities(prev => prev.filter(c => c.id !== city.id));
                                                                                setToast('City deleted', 'success');
                                                                            } catch(err) { setToast(err.message, 'error'); } finally { setConfirmModal(null); }
                                                                        }
                                                                    });
                                                                }}
                                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        {/* Desktop Table View */}
                                        <div className="hidden sm:block">
                                            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                                                <table className="w-full text-sm text-left">
                                                    <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200 sticky top-0 z-10">
                                                        <tr>
                                                            <th className="px-5 py-3">{t('cityName') || 'City Name'}</th>
                                                            <th className="px-5 py-3 w-24 text-center">{t('country') || 'Country'}</th>
                                                            <th className="px-5 py-3 w-24 text-center">{t('status') || 'Status'}</th>
                                                            <th className="px-5 py-3 w-32 text-right">{t('actions') || 'Actions'}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {initialLoading ? (
                                                            [1, 2, 3].map(i => (
                                                                <tr key={i} className="hover:bg-gray-50">
                                                                    <td className="px-5 py-3"><Skeleton width="60%" height={16} animation="wave" /></td>
                                                                    <td className="px-5 py-3 text-center"><Skeleton width={30} height={16} animation="wave" className="mx-auto" /></td>
                                                                    <td className="px-5 py-3 text-center"><Skeleton width={60} height={20} animation="wave" className="mx-auto rounded-full" /></td>
                                                                    <td className="px-5 py-3 flex justify-end"><Skeleton width={60} height={24} animation="wave" className="rounded-md" /></td>
                                                                </tr>
                                                            ))
                                                        ) : cities.filter(c => !citySearch || c.name.toLowerCase().includes(citySearch.toLowerCase())).length === 0 ? (
                                                            <tr>
                                                                <td colSpan="4" className="text-center py-6 text-gray-500">
                                                                    {t('noCitiesFound') || 'No cities found.'}
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            cities.filter(c => !citySearch || c.name.toLowerCase().includes(citySearch.toLowerCase())).map((city) => (
                                                                <tr key={city.id} className="hover:bg-gray-50 transition-colors">
                                                                    <td className="px-5 py-3 font-medium text-gray-900">
                                                                        <div className="flex items-center gap-2">
                                                                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${city.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                                                                            {city.name}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-5 py-3 text-center text-gray-500">{city.country}</td>
                                                                    <td className="px-5 py-3 text-center">
                                                                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${city.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                                                            {city.isActive ? 'Active' : 'Inactive'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-5 py-3">
                                                                        <div className="flex items-center justify-end gap-2">
                                                                            <button
                                                                                onClick={async () => {
                                                                                    try {
                                                                                        const updated = await citiesService.toggleStatus(city.id);
                                                                                        setCities(prev => prev.map(c => c.id === city.id ? updated : c));
                                                                                    } catch(err) { setToast(err.message, 'error'); }
                                                                                }}
                                                                                title={city.isActive ? 'Disable' : 'Enable'}
                                                                                className={`relative w-9 h-5 !min-h-[20px] rounded-full transition-colors focus:outline-none flex-shrink-0 ${city.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                                                                style={{ minWidth: '36px' }}
                                                                            >
                                                                                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${city.isActive ? 'translate-x-4' : 'translate-x-0'}`} />
                                                                            </button>
                                                                            <button
                                                                                onClick={() => {
                                                                                    setConfirmModal({
                                                                                        title: 'Delete City',
                                                                                        message: `Are you sure you want to delete "${city.name}"?`,
                                                                                        confirmText: 'Delete',
                                                                                        danger: true,
                                                                                        onConfirm: async () => {
                                                                                            try {
                                                                                                await citiesService.delete(city.id);
                                                                                                setCities(prev => prev.filter(c => c.id !== city.id));
                                                                                                setToast('City deleted', 'success');
                                                                                            } catch(err) { setToast(err.message, 'error'); } finally { setConfirmModal(null); }
                                                                                        }
                                                                                    });
                                                                                }}
                                                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
                                                <input
                                                    type="text"
                                                    placeholder={t('newCityPlaceholder') || 'New city name...'}
                                                    value={newCityName}
                                                    onChange={(e) => setNewCityName(e.target.value)}
                                                    onKeyDown={async (e) => {
                                                        if (e.key === 'Enter' && newCityName.trim()) {
                                                            try {
                                                                const res = await citiesService.create(newCityName.trim());
                                                                setCities(prev => [...prev, res].sort((a, b) => a.name.localeCompare(b.name)));
                                                                setNewCityName('');
                                                                setToast('City added', 'success');
                                                            } catch(err) { setToast(err.message, 'error'); }
                                                        }
                                                    }}
                                                    className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500 shadow-sm"
                                                />
                                                <button
                                                    onClick={async () => {
                                                        if (!newCityName.trim()) return;
                                                        try {
                                                            const res = await citiesService.create(newCityName.trim());
                                                            setCities(prev => [...prev, res].sort((a, b) => a.name.localeCompare(b.name)));
                                                            setNewCityName('');
                                                            setToast('City added', 'success');
                                                        } catch(err) { setToast(err.message, 'error'); }
                                                    }}
                                                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-1 shrink-0 transition-colors shadow-sm"
                                                >
                                                    <Plus className="w-4 h-4" /> {t('add') || 'Add'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                    </div>
                </div>
            </div>
            )}

            {/* Team Members Table */}
            {activeSection === 'users' && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="px-4 sm:px-5 py-4 border-b bg-gray-50 flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-600" />
                        <h3 className="text-sm font-bold text-gray-800">Team Members</h3>
                        <span className="ml-auto text-xs text-gray-400">{users.length} members</span>
                    </div>

                    {/* Mobile Card View */}
                    <div className="block sm:hidden p-4 space-y-3">
                        {initialLoading ? (
                            [1, 2, 3].map((i) => (
                                <div key={i} className="bg-gray-50 rounded-lg border border-gray-100 p-4 space-y-2">
                                    <Skeleton width={140} height={16} animation="wave" />
                                    <Skeleton width={200} height={13} animation="wave" />
                                    <div className="flex gap-2">
                                        <Skeleton variant="rounded" width={55} height={24} animation="wave" className="rounded-full" />
                                        <Skeleton variant="rounded" width={65} height={24} animation="wave" className="rounded-full" />
                                    </div>
                                </div>
                            ))
                        ) : users.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 text-sm">
                                {t('noTeamMembers') || 'No team members found.'}
                            </div>
                        ) : (
                            users.map((u) => (
                                <div key={u.id} className={`bg-gray-50 rounded-lg border border-gray-100 p-4 ${!u.is_active ? 'opacity-60' : ''}`}>
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-semibold text-gray-900 text-sm truncate">{u.username || u.name}</span>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${u.role === 'CEO'
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : ['GM_Operations', 'BM_Operations'].includes(u.role)
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : u.role === 'Senior_Team_Lead'
                                                                ? 'bg-amber-100 text-amber-800'
                                                                : u.role === 'Team_Lead'
                                                                    ? 'bg-indigo-100 text-indigo-700'
                                                                    : 'bg-green-100 text-green-700'
                                                    }`}>
                                                    {ROLE_LABELS[u.role] || u.role?.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 truncate">{u.email}</p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                onClick={() => handleToggleActive(u)}
                                                disabled={togglingUser === u.id}
                                                className={`relative w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 disabled:opacity-50 ${u.is_active ? 'bg-emerald-500' : 'bg-gray-300'
                                                    }`}
                                                style={{ minWidth: '36px' }}
                                            >
                                                {togglingUser === u.id ? (
                                                    <Loader className="absolute top-[2px] left-[2px] w-4 h-4 text-white animate-spin" />
                                                ) : (
                                                    <span
                                                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${u.is_active ? 'translate-x-4' : 'translate-x-0'
                                                            }`}
                                                    />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleRemoveUser(u)}
                                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-md transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                                        <button
                                            onClick={() => setEditUserModal(u)}
                                            className="inline-flex flex-1 justify-center items-center gap-1.5 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg text-xs font-medium transition-colors"
                                        >
                                            <Edit2 className="w-3 h-3" />
                                            {t('editRole') || 'Edit Role'}
                                        </button>
                                        <button
                                            onClick={() => handleResetPassword(u)}
                                            disabled={passwordLoading}
                                            className="inline-flex flex-1 justify-center items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-xs font-medium transition-colors"
                                        >
                                            {passwordLoading ? <Loader className="w-3 h-3 animate-spin" /> : <Key className="w-3 h-3" />}
                                            {t('resetBtn') || 'Reset'}
                                        </button>
                                        <button
                                            onClick={() => { setPasswordModal({ userId: u.id, userName: u.username || u.email, mode: 'change' }); setNewPassword(''); setRevealedPassword(null); }}
                                            className="inline-flex flex-1 justify-center items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors"
                                        >
                                            <Lock className="w-3 h-3" />
                                            {t('changeBtn') || 'Change'}
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                                <tr>
                                    <th className="px-5 py-3">{t('name')}</th>
                                    <th className="px-5 py-3">{t('email')}</th>
                                    <th className="px-5 py-3">{t('roleLabel') || 'Role'}</th>
                                    <th className="px-5 py-3">{t('reportsTo') || 'Reports To'}</th>
                                    <th className="px-5 py-3 text-center">{t('status') || 'Status'}</th>
                                    <th className="px-5 py-3 text-center">{t('password') || 'Password'}</th>
                                    <th className="px-5 py-3 text-right">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {initialLoading ? (
                                    [1, 2, 3, 4, 5].map((i) => (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="px-5 py-3"><Skeleton width={120} height={16} animation="wave" /></td>
                                            <td className="px-5 py-3"><Skeleton width={180} height={16} animation="wave" /></td>
                                            <td className="px-5 py-3"><Skeleton variant="rounded" width={60} height={24} animation="wave" className="rounded-full" /></td>
                                            <td className="px-5 py-3"><Skeleton width={120} height={16} animation="wave" /></td>
                                            <td className="px-5 py-3"><Skeleton width={70} height={28} animation="wave" className="rounded-full mx-auto" /></td>
                                            <td className="px-5 py-3"><Skeleton width={120} height={28} animation="wave" className="rounded-lg mx-auto" /></td>
                                            <td className="px-5 py-3 flex justify-end"><Skeleton width={24} height={24} animation="wave" /></td>
                                        </tr>
                                    ))
                                ) : users.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-8 text-gray-500">
                                            {t('noTeamMembers') || 'No team members found.'}
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((u) => (
                                        <tr key={u.id} className={`hover:bg-gray-50 transition-colors ${!u.is_active ? 'opacity-60' : ''}`}>
                                            <td className="px-5 py-3 font-medium text-gray-900">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${u.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                                                    {u.username || u.name}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-gray-500 text-xs">{u.email}</td>
                                            <td className="px-5 py-3">
                                                <span className={`px-2 py-1 rounded-full text-xs font-bold inline-block whitespace-nowrap ${u.role === 'CEO'
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : ['GM_Operations', 'BM_Operations'].includes(u.role)
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : u.role === 'Senior_Team_Lead'
                                                                ? 'bg-amber-100 text-amber-800'
                                                                : u.role === 'Team_Lead'
                                                                    ? 'bg-indigo-100 text-indigo-700'
                                                                    : 'bg-green-100 text-green-700'
                                                    }`}>
                                                    {ROLE_LABELS[u.role] || u.role?.replace(/_/g, ' ')}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-xs font-semibold text-gray-500">
                                                {u.parentId ? (users.find(p => p.id === u.parentId)?.username || users.find(p => p.id === u.parentId)?.email || u.parentId) : '-'}
                                            </td>
                                            <td className="px-5 py-3 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <span className={`text-[10px] font-bold ${u.is_active ? 'text-emerald-600' : 'text-gray-400'}`}>
                                                        {u.is_active ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}
                                                    </span>
                                                    <button
                                                        onClick={() => handleToggleActive(u)}
                                                        disabled={togglingUser === u.id || !canUserManage(u)}
                                                        className={`relative w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 disabled:opacity-50 ${u.is_active ? 'bg-emerald-500' : 'bg-gray-300'
                                                            }`}
                                                        style={{ minWidth: '36px' }}
                                                    >
                                                        {togglingUser === u.id ? (
                                                            <Loader className="absolute top-[2px] left-[2px] w-4 h-4 text-white animate-spin" />
                                                        ) : (
                                                            <span
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${u.is_active ? 'translate-x-4' : 'translate-x-0'
                                                                    }`}
                                                            />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {canUserManage(u) ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleResetPassword(u)}
                                                                disabled={passwordLoading}
                                                                title="Reset & View Password"
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-xs font-medium transition-colors"
                                                            >
                                                                {passwordLoading ? <Loader className="w-3 h-3 animate-spin" /> : <Key className="w-3 h-3" />}
                                                                {t('resetBtn') || 'Reset'}
                                                            </button>
                                                            <button
                                                                onClick={() => { setPasswordModal({ userId: u.id, userName: u.username || u.email, mode: 'change' }); setNewPassword(''); setRevealedPassword(null); }}
                                                                title="Set Custom Password"
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors"
                                                            >
                                                                <Lock className="w-3 h-3" />
                                                                {t('changeBtn') || 'Change'}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">-</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {canUserManage(u) ? (
                                                        <>
                                                            <button
                                                                onClick={() => setEditUserModal(u)}
                                                                className="p-1.5 text-purple-500 hover:bg-purple-50 rounded-md transition-colors"
                                                                title="Edit Role"
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleRemoveUser(u)}
                                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                                                title={t('revokeAccessQuestion') || 'Revoke Access'}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">-</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Password Modal */}
                    {passwordModal && (
                        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="bg-white rounded-2xl shadow-2xl border border-blue-100 w-full max-w-sm overflow-hidden">
                                <div className="p-5 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                                            <Key className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-blue-900 text-sm">
                                                {passwordModal.mode === 'view' ? (t('passwordReset') || 'Password Reset') : (t('changePassword') || 'Change Password')}
                                            </h3>
                                            <p className="text-xs text-blue-600">{passwordModal.userName}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => { setPasswordModal(null); setRevealedPassword(null); setNewPassword(''); }} className="p-1.5 hover:bg-blue-100 rounded-lg">
                                        <X className="w-4 h-4 text-blue-600" />
                                    </button>
                                </div>

                                <div className="p-5 space-y-4">
                                    {/* View mode — show reset password */}
                                    {passwordModal.mode === 'view' && revealedPassword && (
                                        <div>
                                            <p className="text-xs text-gray-500 mb-3">
                                                {t('passwordResetSuccessMsg') || 'Password has been reset. Share this temporary password with the user — they will be prompted to change it on next login.'}
                                            </p>
                                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                                <label className="block text-xs font-bold text-amber-700 mb-2 uppercase tracking-wider">{t('newTempPassword') || 'New Temporary Password'}</label>
                                                <div className="flex items-center gap-2">
                                                    <code className="flex-1 font-mono text-lg font-bold text-amber-900 bg-white px-3 py-2 rounded-lg border border-amber-200">
                                                        {revealedPassword.password}
                                                    </code>
                                                    <CopyButton text={revealedPassword.password} />
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setPasswordModal(prev => ({ ...prev, mode: 'change' }))}
                                                className="w-full mt-3 py-2.5 border border-blue-200 text-blue-600 hover:bg-blue-50 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                            >
                                                <Lock className="w-4 h-4" /> {t('setCustomPasswordInstead') || 'Set Custom Password Instead'}
                                            </button>
                                        </div>
                                    )}

                                    {/* Change mode ΓÇö set custom password */}
                                    {passwordModal.mode === 'change' && (
                                        <div>
                                            <p className="text-xs text-gray-500 mb-3">
                                                {t('setNewPasswordFor') || 'Set a new password for'} <strong>{passwordModal.userName}</strong>. {t('min6Chars') || 'Minimum 6 characters.'}
                                            </p>
                                            <div className="relative">
                                                <input
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    placeholder={t('enterNewPassword') || "Enter new password..."}
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleChangePassword()}
                                                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                                                >
                                                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                            {newPassword.length > 0 && newPassword.length < 6 && (
                                                <p className="text-xs text-red-500 mt-1">{t('min6CharsError') || 'Password must be at least 6 characters'}</p>
                                            )}
                                            <button
                                                onClick={handleChangePassword}
                                                disabled={passwordLoading || newPassword.length < 6}
                                                className="w-full mt-3 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
                                            >
                                                {passwordLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                                                {t('setPassword') || 'Set Password'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Edit User Role Modal */}
                    {editUserModal && (
                        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="bg-white rounded-2xl shadow-2xl border border-purple-100 w-full max-w-sm overflow-visible">
                                <div className="p-5 bg-purple-50 rounded-t-2xl border-b border-purple-100 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                                            <Shield className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-purple-900 text-sm">{t('editUserRole') || 'Edit User Role'}</h3>
                                            <p className="text-xs text-purple-600">{editUserModal.username || editUserModal.email}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setEditUserModal(null)} className="p-1.5 hover:bg-purple-100 rounded-lg">
                                        <X className="w-4 h-4 text-purple-600" />
                                    </button>
                                </div>
                                <div className="p-5 pb-40 space-y-4">
                                    {/* Name field — CEO only */}
                                    {activeUser?.role === 'CEO' && (
                                        <div>
                                            <label className="block text-xs font-bold text-purple-700 mb-2">Name</label>
                                            <input
                                                type="text"
                                                value={editUserModal.username || ""}
                                                onChange={(e) => setEditUserModal({ ...editUserModal, username: e.target.value })}
                                                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                                                placeholder="Enter name"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-xs font-bold text-purple-700 mb-2">{t('roleLabel') || 'Role'}</label>
                                        <Dropdown>
                                            <DropdownTrigger>
                                                <button type="button" className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                    {ROLE_LABELS[editUserModal.role] || editUserModal.role?.replace(/_/g, ' ')}
                                                    <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                </button>
                                            </DropdownTrigger>
                                            <DropdownPopover className="w-full">
                                                <DropdownMenu
                                                    selectedKeys={new Set([editUserModal.role])}
                                                    selectionMode="single"
                                                    onSelectionChange={(keys) => setEditUserModal({ ...editUserModal, role: Array.from(keys)[0] })}
                                                >
                                                    {ROLES.filter(r => {
                                                        if (['GM_Operations', 'BM_Operations'].includes(activeUser?.role)) {
                                                            return ['Senior_Team_Lead', 'Team_Lead', 'Associate_Team_Lead'].includes(r);
                                                        }
                                                        if (['Team_Lead', 'Senior_Team_Lead'].includes(activeUser?.role)) {
                                                            return ['Associate_Team_Lead'].includes(r);
                                                        }
                                                        return true;
                                                    }).map((role) => (
                                                        <DropdownItem key={role} id={role}>{ROLE_LABELS[role] || role}</DropdownItem>
                                                    ))}
                                                </DropdownMenu>
                                            </DropdownPopover>
                                        </Dropdown>
                                    </div>
                                    {editUserModal && ['Associate_Team_Lead', 'Associate'].includes(editUserModal.role) && ['CEO', 'GM_Operations', 'BM_Operations'].includes(activeUser?.role) && (
                                        <div>
                                            <label className="block text-xs font-bold text-purple-700 mb-2">
                                                {t('selectParentTeamLead') || 'Select Parent Team Lead / Senior Team Lead'}
                                            </label>
                                            <Dropdown>
                                                <DropdownTrigger>
                                                    <button type="button" className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                        {users.find(u => u.id === parseInt(editUserModal.parentId))?.username || (t('unassigned') || 'Unassigned / None')}
                                                        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                    </button>
                                                </DropdownTrigger>
                                                <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                                                    <DropdownMenu
                                                        selectedKeys={new Set([String(editUserModal.parentId || '')])}
                                                        selectionMode="single"
                                                        onSelectionChange={(keys) => {
                                                            const selected = Array.from(keys)[0];
                                                            setEditUserModal({ ...editUserModal, parentId: selected ? parseInt(selected) : null });
                                                        }}
                                                    >
                                                        <DropdownItem id="">
                                                            {t('unassigned') || 'Unassigned / None'}
                                                        </DropdownItem>
                                                        {users
                                                            .filter(u => u.is_active && ['Team_Lead', 'Senior_Team_Lead'].includes(u.role) && (activeUser?.role === "CEO" || u.parentId === Number(activeUser?.id || activeUser?.uid)))
                                                            .map(u => (
                                                                <DropdownItem key={u.id} id={String(u.id)}>
                                                                    {u.username} ({ROLE_LABELS[u.role] || u.role}) - {u.email}
                                                                </DropdownItem>
                                                            ))
                                                        }
                                                    </DropdownMenu>
                                                </DropdownPopover>
                                            </Dropdown>
                                        </div>
                                    )}
                                    {editUserModal && ['Team_Lead', 'Senior_Team_Lead'].includes(editUserModal.role) && activeUser?.role === 'CEO' && (
                                        <div>
                                            <label className="block text-xs font-bold text-purple-700 mb-2">
                                                {t('selectParentGMOperations') || 'Select Parent GM Operations'}
                                            </label>
                                            <Dropdown>
                                                <DropdownTrigger>
                                                    <button type="button" className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                        {users.find(u => u.id === parseInt(editUserModal.parentId))?.username || (t('unassigned') || 'Unassigned / None')}
                                                        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                    </button>
                                                </DropdownTrigger>
                                                <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                                                    <DropdownMenu
                                                        selectedKeys={new Set([String(editUserModal.parentId || '')])}
                                                        selectionMode="single"
                                                        onSelectionChange={(keys) => {
                                                            const selected = Array.from(keys)[0];
                                                            setEditUserModal({ ...editUserModal, parentId: selected ? parseInt(selected) : null });
                                                        }}
                                                    >
                                                        <DropdownItem id="">
                                                            {t('unassigned') || 'Unassigned / None'}
                                                        </DropdownItem>
                                                        {users
                                                            .filter(u => u.is_active && ['GM_Operations', 'BM_Operations'].includes(u.role))
                                                            .map(u => (
                                                                <DropdownItem key={u.id} id={String(u.id)}>
                                                                    {u.username} ({u.email})
                                                                </DropdownItem>
                                                            ))
                                                        }
                                                    </DropdownMenu>
                                                </DropdownPopover>
                                            </Dropdown>
                                        </div>
                                    )}
                                    <button
                                        onClick={handleUpdateUserRole}
                                        disabled={editUserLoading}
                                        className="w-full mt-3 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
                                    >
                                        {editUserLoading ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                        {t('saveChanges') || 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Invite User Modal */}
                    {showInviteModal && (
                        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                            <div className="bg-white rounded-2xl shadow-2xl border border-purple-100 w-full max-w-md overflow-visible">
                                <div className="p-5 bg-purple-50 rounded-t-2xl border-b border-purple-100 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                                            <UserPlus className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-purple-900 text-sm">{t('inviteTeamMember') || 'Invite Team Member'}</h3>
                                            <p className="text-xs text-purple-600">Add a new member to your team organization</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setShowInviteModal(false)} className="p-1.5 hover:bg-purple-100 rounded-lg">
                                        <X className="w-4 h-4 text-purple-600" />
                                    </button>
                                </div>
                                <div className="p-5 pb-20 space-y-4">
                                    {/* CEO: 4 tabs: Associate TL, Team Lead, Senior TL, GM Operations */}
                                    {activeUser?.role === 'CEO' && (
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 p-1 bg-purple-50/70 border border-purple-100 rounded-xl mb-4">
                                            <button
                                                type="button"
                                                onClick={() => handleInviteTabChange('associate_team_lead')}
                                                className={`py-2 px-1 rounded-lg text-xs font-bold transition-all text-center ${
                                                    inviteTab === 'associate_team_lead'
                                                        ? 'bg-white text-purple-700 shadow-sm'
                                                        : 'text-gray-600 hover:text-gray-900'
                                                }`}
                                            >
                                                Associate TL
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleInviteTabChange('team_lead')}
                                                className={`py-2 px-1 rounded-lg text-xs font-bold transition-all text-center ${
                                                    inviteTab === 'team_lead'
                                                        ? 'bg-white text-purple-700 shadow-sm'
                                                        : 'text-gray-600 hover:text-gray-900'
                                                }`}
                                            >
                                                Team Lead
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleInviteTabChange('senior_team_lead')}
                                                className={`py-2 px-1 rounded-lg text-xs font-bold transition-all text-center ${
                                                    inviteTab === 'senior_team_lead'
                                                        ? 'bg-white text-purple-700 shadow-sm'
                                                        : 'text-gray-600 hover:text-gray-900'
                                                }`}
                                            >
                                                Senior TL
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleInviteTabChange('gm_operations')}
                                                className={`py-2 px-1 rounded-lg text-xs font-bold transition-all text-center ${
                                                    inviteTab === 'gm_operations'
                                                        ? 'bg-white text-purple-700 shadow-sm'
                                                        : 'text-gray-600 hover:text-gray-900'
                                                }`}
                                            >
                                                GM Operations
                                            </button>
                                        </div>
                                    )}

                                    {/* GM Operations: 3 tabs: Associate TL, Team Lead, Senior TL */}
                                    {['GM_Operations', 'BM_Operations'].includes(activeUser?.role) && (
                                        <div className="grid grid-cols-3 gap-1.5 p-1 bg-purple-50/70 border border-purple-100 rounded-xl mb-4">
                                            <button
                                                type="button"
                                                onClick={() => handleInviteTabChange('associate_team_lead')}
                                                className={`py-2.5 px-2 rounded-lg text-xs font-bold transition-all text-center ${
                                                    inviteTab === 'associate_team_lead'
                                                        ? 'bg-white text-purple-700 shadow-sm'
                                                        : 'text-gray-600 hover:text-gray-900'
                                                }`}
                                            >
                                                Associate TL
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleInviteTabChange('team_lead')}
                                                className={`py-2.5 px-2 rounded-lg text-xs font-bold transition-all text-center ${
                                                    inviteTab === 'team_lead'
                                                        ? 'bg-white text-purple-700 shadow-sm'
                                                        : 'text-gray-600 hover:text-gray-900'
                                                }`}
                                            >
                                                Team Lead
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleInviteTabChange('senior_team_lead')}
                                                className={`py-2.5 px-2 rounded-lg text-xs font-bold transition-all text-center ${
                                                    inviteTab === 'senior_team_lead'
                                                        ? 'bg-white text-purple-700 shadow-sm'
                                                        : 'text-gray-600 hover:text-gray-900'
                                                }`}
                                            >
                                                Senior TL
                                            </button>
                                        </div>
                                    )}

                                    <form onSubmit={handleAddUser} className="space-y-4">
                                        <div className="flex flex-col">
                                            <label className="text-xs font-bold text-gray-600 mb-1.5">{t('name')}</label>
                                            <input
                                                className="px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-100 focus:border-purple-500"
                                                placeholder="Enter name..."
                                                value={newUserName}
                                                onChange={(e) => setNewUserName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-xs font-bold text-gray-600 mb-1.5">{t('email')}</label>
                                            <input
                                                className="px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-100 focus:border-purple-500"
                                                placeholder="Enter email..."
                                                type="email"
                                                value={newUserEmail}
                                                onChange={(e) => setNewUserEmail(e.target.value)}
                                                required
                                            />
                                        </div>

                                        {/* Parent Select for Associate Team Lead ONLY when invited by CEO or GM Operations */}
                                        {['associate_team_lead', 'associate'].includes(inviteTab) && ['CEO', 'GM_Operations', 'BM_Operations'].includes(activeUser?.role) && (
                                            <div className="flex flex-col">
                                                <label className="text-xs font-bold text-gray-600 mb-1.5">
                                                    {t('selectParentTeamLead') || 'Select Parent Team Lead / Senior Team Lead'}
                                                </label>
                                                <Dropdown>
                                                    <DropdownTrigger>
                                                        <button type="button" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-white flex justify-between items-center text-left font-medium">
                                                            {users.find(u => u.id === parseInt(newUserParentId))?.username || (t('unassigned') || 'Unassigned / None')}
                                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                        </button>
                                                    </DropdownTrigger>
                                                    <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                                                        <DropdownMenu
                                                            selectedKeys={new Set([String(newUserParentId || '')])}
                                                            selectionMode="single"
                                                            onSelectionChange={(keys) => setNewUserParentId(Array.from(keys)[0])}
                                                        >
                                                            <DropdownItem id="">
                                                                {t('unassigned') || 'Unassigned / None'}
                                                            </DropdownItem>
                                                            {users
                                                                .filter(u => u.is_active && ['Team_Lead', 'Senior_Team_Lead'].includes(u.role) && (activeUser?.role === "CEO" || u.parentId === Number(activeUser?.id || activeUser?.uid)))
                                                                .map(u => (
                                                                    <DropdownItem key={u.id} id={String(u.id)}>
                                                                        {u.username} ({ROLE_LABELS[u.role] || u.role}) - {u.email}
                                                                    </DropdownItem>
                                                                ))
                                                            }
                                                        </DropdownMenu>
                                                    </DropdownPopover>
                                                </Dropdown>
                                            </div>
                                        )}

                                        {/* Parent Select for Team Lead or Senior Team Lead ONLY when invited by CEO */}
                                        {['team_lead', 'senior_team_lead'].includes(inviteTab) && activeUser?.role === 'CEO' && (
                                            <div className="flex flex-col">
                                                <label className="text-xs font-bold text-gray-600 mb-1.5">
                                                    {t('selectParentGMOperations') || 'Select Parent GM Operations'}
                                                </label>
                                                <Dropdown>
                                                    <DropdownTrigger>
                                                        <button type="button" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-white flex justify-between items-center text-left font-medium">
                                                            {users.find(u => u.id === parseInt(newUserParentId))?.username || (t('unassigned') || 'Unassigned / None')}
                                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                        </button>
                                                    </DropdownTrigger>
                                                    <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                                                        <DropdownMenu
                                                            selectedKeys={new Set([String(newUserParentId)])}
                                                            selectionMode="single"
                                                            onSelectionChange={(keys) => setNewUserParentId(Array.from(keys)[0])}
                                                        >
                                                            <DropdownItem id="">
                                                                {t('unassigned') || 'Unassigned / None'}
                                                            </DropdownItem>
                                                            {users
                                                                .filter(u => u.is_active && ['GM_Operations', 'BM_Operations'].includes(u.role))
                                                                .map(u => (
                                                                    <DropdownItem key={u.id} id={String(u.id)}>
                                                                        {u.username} ({u.email})
                                                                    </DropdownItem>
                                                                ))
                                                            }
                                                        </DropdownMenu>
                                                    </DropdownPopover>
                                                </Dropdown>
                                            </div>
                                        )}

                                        <div className="pt-2">
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-purple-200 transition-all transform active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
                                            >
                                                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                                {['associate_team_lead', 'associate'].includes(inviteTab)
                                                    ? (t('inviteAssociateTeamLead') || 'Invite Associate Team Lead')
                                                    : inviteTab === 'senior_team_lead'
                                                    ? (t('inviteSeniorTeamLead') || 'Invite Senior Team Lead')
                                                    : ['gm_operations', 'bm_operations'].includes(inviteTab)
                                                    ? (t('inviteGmOperations') || 'Invite GM Operations')
                                                    : (t('inviteTeamLead') || 'Invite Team Lead')}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* CSV Import Modals */}
            {showCsvSetup && (
                <CsvSetupModal
                    isOpen={true}
                    importType="users"
                    onClose={() => setShowCsvSetup(false)}
                    onStartImport={handleCsvUploadStart}
                />
            )}
            
            {showCsvImport && (
                <CsvImportModal
                    importType="users"
                    progress={importProgress}
                    onClose={() => {
                        setShowCsvImport(false);
                        closeImportModal();
                    }}
                />
            )}
        </div>
    );
}
